import type { QuerySpec } from '@helios/powersync-client-core';

/* ------------------------------------------------------------------ */
/*  Dermatology AI Image Analysis (PRD.217)                           */
/*                                                                    */
/*  Client-side QuerySpecs against the slim PowerSync mirror of:      */
/*    - ai_analysis_runs           (raw_response_json is server-only) */
/*    - ai_analysis_run_images                                        */
/*    - patient_diagnosis_suggestions                                 */
/*                                                                    */
/*  Note: external_ai_usage_logs is server-only and never reaches the */
/*  client; there is no QuerySpec for it here by design.              */
/* ------------------------------------------------------------------ */

// ─── Row Types ───

export interface AiAnalysisRunRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;

  feature: string | null;
  provider: string | null;
  provider_mode: string | null;
  model_version: string | null;

  status: string | null; // 'pending' | 'completed' | 'failed'
  images_submitted: number | null;
  aggregation: string | null;

  primary_diagnosis: string | null;
  confidence: number | null;
  severity: string | null;
  agreement: number | null;

  symptoms_json: string | null; // jsonb stringified on client
  treatment_json: string | null;
  top_predictions_json: string | null;
  per_image_results_json: string | null;
  embedding_shape_json: string | null;

  disclaimer: string | null;

  requested_by: string | null;
  requested_at: string | null;
  completed_at: string | null;
  error_message: string | null;

  created_at: string | null;
  updated_at: string | null;
}

export interface AiAnalysisRunImageRow {
  id: string;
  analysis_run_id: string | null;
  encounter_image_id: string | null;
  filename: string | null;
  sort_order: number | null;
  created_at: string | null;
}

export type PatientDiagnosisSuggestionStatus = 'suggested' | 'accepted' | 'dismissed';

export interface PatientDiagnosisSuggestionRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  ai_analysis_run_id: string | null;

  source_provider: string | null;

  diagnosis_text: string | null;
  condition: string | null;
  severity: string | null;
  confidence: number | null;

  status: PatientDiagnosisSuggestionStatus | string | null;

  accepted_label: string | null;
  accepted_diagnosis_entry_id: string | null;
  accepted_by: string | null;
  accepted_at: string | null;

  dismissed_by: string | null;
  dismissed_at: string | null;

  created_at: string | null;
  updated_at: string | null;
}

// ─── Queries ───

/** All analysis runs for an encounter, newest first. */
export function qAnalysisRunsByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['ai_analysis_runs', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM ai_analysis_runs
          WHERE encounter_id = ?
          ORDER BY requested_at DESC`,
    params: [encounterId],
    watch: ['ai_analysis_runs'],
  };
}

/** Most recent completed analysis run for an encounter, or null. */
export function qLatestCompletedAnalysisRunByEncounter(
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['ai_analysis_runs', 'latest-completed-by-encounter', encounterId] as const,
    sql: `SELECT * FROM ai_analysis_runs
          WHERE encounter_id = ? AND status = ?
          ORDER BY completed_at DESC
          LIMIT 1`,
    params: [encounterId, 'completed'],
    watch: ['ai_analysis_runs'],
  };
}

/** Image links for a given analysis run, ordered. */
export function qAnalysisRunImagesByRun(analysisRunId: string): QuerySpec<[string]> {
  return {
    key: ['ai_analysis_run_images', 'by-run', analysisRunId] as const,
    sql: `SELECT * FROM ai_analysis_run_images
          WHERE analysis_run_id = ?
          ORDER BY sort_order ASC, created_at ASC`,
    params: [analysisRunId],
    watch: ['ai_analysis_run_images'],
  };
}

/** All diagnosis suggestions for an encounter (any status), newest first. */
export function qDiagnosisSuggestionsByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_diagnosis_suggestions', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_diagnosis_suggestions
          WHERE encounter_id = ?
          ORDER BY created_at DESC`,
    params: [encounterId],
    watch: ['patient_diagnosis_suggestions'],
  };
}

/** Active (still-pending) diagnosis suggestions for an encounter. */
export function qPendingDiagnosisSuggestionsByEncounter(
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['patient_diagnosis_suggestions', 'pending-by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_diagnosis_suggestions
          WHERE encounter_id = ? AND status = ?
          ORDER BY created_at DESC`,
    params: [encounterId, 'suggested'],
    watch: ['patient_diagnosis_suggestions'],
  };
}

// ─── Client-side mutation SQL (executed via PowerSync db.execute) ──────────
//
// These helpers return prepared statements + params for use with the local
// PowerSync DB. The writes flow back through SyncWrite to Postgres, where the
// readOnlyColumns guard on `patient_diagnosis_suggestions` prevents the client
// from touching anything outside the lifecycle fields below.
//
// Suggestion rows are *created* server-side by the dermatology analysis Cloud
// Function — there is no client-side insert helper here by design.

/**
 * Accept (or Edit-then-accept) a suggestion. `acceptedLabel` is the final text
 * the clinician confirms; pass `diagnosis_text` unchanged for plain Accept, or
 * the edited string for Edit. `acceptedDiagnosisEntryId` should be the id of
 * the `patient_diagnosis_entries` row the UI inserts in the same transaction.
 */
export function acceptDiagnosisSuggestionStmt(input: {
  suggestionId: string;
  acceptedLabel: string;
  acceptedDiagnosisEntryId: string;
  userId: string;
  now: string;
}): { sql: string; params: [string, string, string, string, string, string] } {
  return {
    sql: `UPDATE patient_diagnosis_suggestions
          SET status = 'accepted',
              accepted_label = ?,
              accepted_diagnosis_entry_id = ?,
              accepted_by = ?,
              accepted_at = ?,
              updated_at = ?
          WHERE id = ?`,
    params: [
      input.acceptedLabel,
      input.acceptedDiagnosisEntryId,
      input.userId,
      input.now,
      input.now,
      input.suggestionId,
    ],
  };
}

/** Dismiss a suggestion. */
export function dismissDiagnosisSuggestionStmt(input: {
  suggestionId: string;
  userId: string;
  now: string;
}): { sql: string; params: [string, string, string, string] } {
  return {
    sql: `UPDATE patient_diagnosis_suggestions
          SET status = 'dismissed',
              dismissed_by = ?,
              dismissed_at = ?,
              updated_at = ?
          WHERE id = ?`,
    params: [input.userId, input.now, input.now, input.suggestionId],
  };
}
